const SELECTORS = {
    panel: '.favorites-panel',
    toggleBtn: '.js-toggle-favorites',
    addBtn: '.js-add-to-favorites',
    deleteBtn: '.js-delete-bookmark',
    emailBtn: '.js-email-favorites',
    backdrop: '.favorites-backdrop',
    bookmarksList: '.favorites-panel__bookmarks',
    favoriteNumber: '.favorites-toggle__number',
    message: '.favorites__message',
};

const CLASSES = {
    isOpen: 'is-open',
    isShown: 'is-shown',
};

const STORAGE_KEY = 'rvu-favorites';
const MESSAGE_DURATION = 2000;

// Lenis instance - should be available globally
let lenisInstance = null;

// Store event listeners for cleanup
const eventListeners = new Map();

// Store Lenis instance reference
export const setLenisInstance = (lenis) => {
    lenisInstance = lenis;
};

// HTML escape function to prevent XSS
const escapeHtml = (unsafe) => {
    if (typeof unsafe !== 'string') return '';

    return unsafe.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');
};

// Get bookmarks from localStorage
const getBookmarks = () => {
    try {
        const bookmarks = localStorage.getItem(STORAGE_KEY);
        if (!bookmarks) return [];

        const parsed = JSON.parse(bookmarks);

        if (!Array.isArray(parsed)) {
            console.warn('Invalid bookmarks format, resetting');
            localStorage.removeItem(STORAGE_KEY);

            return [];
        }

        return parsed.filter((bookmark) => {
            if (typeof bookmark !== 'object' || bookmark === null) return false;
            if (typeof bookmark.url !== 'string') return false;
            if (bookmark.type === 'ranch') {
                return typeof bookmark.name === 'string' && typeof bookmark.address === 'string';
            }

            return typeof bookmark.title === 'string';
        });
    } catch (error) {
        console.error('Error reading bookmarks from localStorage:', error);
        localStorage.removeItem(STORAGE_KEY);

        return [];
    }
};

// Save bookmarks to localStorage
const saveBookmarks = (bookmarks) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(bookmarks));

        return true;
    } catch (error) {
        console.error('Error saving bookmarks to localStorage:', error);

        return false;
    }
};

// Check if bookmark exists
const isBookmarkExists = (url) => {
    const bookmarks = getBookmarks();

    return bookmarks.some((bookmark) => bookmark.url === url);
};

const validateRanchData = (data) => {
    if (!data || typeof data !== 'object') return false;

    const requiredFields = [
        'url',
        'image',
        'name',
        'address',
        'acreage',
        'acreageLabel',
        'price',
    ];

    return requiredFields.every((field) => typeof data[field] === 'string');
};

const getRanchData = () => {
    const ranchDataScript = document.getElementById('ranch-data');
    if (ranchDataScript) {
        try {
            const parsed = JSON.parse(ranchDataScript.textContent);
            const data = Array.isArray(parsed) ? parsed[0] : parsed;

            if (validateRanchData(data)) {
                return data;
            }
            console.warn('Invalid ranch data structure');

            return null;
        } catch (error) {
            console.error('Error parsing ranch data:', error);

            return null;
        }
    }

    return null;
};

// Get current page data for bookmark
const getCurrentPageData = () => {
    const ranchData = getRanchData();

    if (ranchData) {
        return {
            type: 'ranch',
            ...ranchData,
        };
    }

    return {
        type: 'page',
        url: window.location.pathname,
        title: document.title,
    };
};

// Create ranch card HTML (with sanitized data)
const createRanchCard = (data) => {
    const typesHTML =
        data.types && data.types.length > 0
            ? data.types
                  .map(
                      (type) => `
            <span class="flex h-10 w-10 items-center justify-center">
                <img src="${escapeHtml(type.icon)}" alt="${escapeHtml(type.label)}" class="h-10 w-10 object-contain">
            </span>
        `,
                  )
                  .join('')
            : '';

    return `
        <a href="${escapeHtml(data.url)}" class="!no-underline group block @container">
            <div class="relative aspect-4/3 overflow-hidden">
                <div class="grid h-full w-full">
                    <img
                    src="${escapeHtml(data.image)}"
                    alt="${escapeHtml(data.imageAlt || data.name)}"
                    class="h-full w-full object-cover transition duration-300 group-hover:scale-105 absolute inset-0 z-0">
                    <div class="absolute inset-0 z-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>
                    <div class="absolute w-full h-full inset-0 z-2 bg-burnt-orange/30 opacity-0 transition duration-300 pointer-none lg:group-hover:opacity-100"></div>
                    <span class="flex h-12 lg:h-20 w-12 lg:w-20 items-center justify-center rounded-full bg-white
                    text-charcoal opacity-0 transition duration-300 pointer-none lg:group-hover:opacity-100 z-30 absolute
                    top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                        <svg class="h-4 lg:h-8 w-4 lg:w-8 -rotate-90 !text-burnt-orange" viewBox="0 0 15 9" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12.5 0L7.13375 5.36625L1.7675 0L0 1.7675L7.13375 8.90125L14.2675 1.7675L12.5 0Z" fill="currentColor" />
                        </svg>
                    </span>
                    <div class="relative z-10 flex h-full flex-col justify-end gap-3 p-2 lg:p-5 text-white">
                        ${typesHTML ? `<div class="flex flex-wrap gap-4">${typesHTML}</div>` : ''}
                        <div class="flex flex-col gap-2 @[450px]:flex-row @[450px]:items-end @[450px]:justify-between @[450px]:gap-4 relative z-3">
                            <div class="space-y-1">
                                <h3 class="text-2xl font-serif uppercase">${escapeHtml(data.name)}</h3>
                                <p class="text-sm text-white">${escapeHtml(data.address)}</p>
                            </div>
                            <div class="@[450px]:text-right">
                                <p class="text-2xl font-serif font-bold uppercase">${escapeHtml(data.price)}</p>
                                <p class="text-sm text-white">${escapeHtml(data.acreage)} ${escapeHtml(data.acreageLabel)}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </a>
    `;
};

// Create simple page link HTML (with sanitized data)
const createPageLink = (data) => {
    return `
        <a href="${escapeHtml(data.url)}" class="text-2xl font-serif uppercase !no-underline hover:underline">
            ${escapeHtml(data.title)}
        </a>
    `;
};

// Create bookmark HTML (with sanitized data)
const createBookmarkHTML = (bookmark, index) => {
    const contentHTML = bookmark.type === 'ranch' ? createRanchCard(bookmark) : createPageLink(bookmark);

    return `
        <div class="favorites-panel__bookmark flex items-start gap-4" data-bookmark-index="${index}">
            <div class="grow">
                ${contentHTML}
            </div>
            <button class="js-delete-bookmark p-2 cursor-pointer hover:bg-white/10 rounded transition-colors"
                    role="button"
                    aria-label="Delete bookmark for ${escapeHtml(bookmark.name || bookmark.title)}"
                    data-bookmark-index="${index}">
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg" class="rotate-45">
                    <path d="M6.41667 1V6.41667V11.8333M11.8333 6.41667H1" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            </button>
        </div>
    `;
};

// Update bookmarks display
const updateBookmarksDisplay = (panel) => {
    const bookmarks = getBookmarks();
    const emptyState = panel.querySelector('.favorites-empty-state');
    const bookmarksList = panel.querySelector('.favorites-panel__bookmarks');

    if (!bookmarksList) {
        console.error('Bookmarks list container not found');

        return;
    }

    if (bookmarks.length === 0) {
        if (emptyState) emptyState.classList.remove('hidden');
        bookmarksList.innerHTML = '';
    } else {
        if (emptyState) emptyState.classList.add('hidden');

        const existingBookmarks = Array.from(bookmarksList.querySelectorAll('.favorites-panel__bookmark'));

        const needsRebuild =
            existingBookmarks.length !== bookmarks.length ||
            bookmarks.some((bookmark, index) => {
                const existing = existingBookmarks[index];
                if (!existing) return true;

                return parseInt(existing.dataset.bookmarkIndex, 10) !== index;
            });

        if (needsRebuild) {
            bookmarksList.innerHTML = bookmarks.map((bookmark, index) => createBookmarkHTML(bookmark, index)).join('');
        }
    }

    updateBookmarkCounter();
};

// Update bookmark counter
const updateBookmarkCounter = () => {
    const bookmarks = getBookmarks();
    const counters = document.querySelectorAll(SELECTORS.favoriteNumber);
    counters.forEach((counter) => {
        counter.textContent = bookmarks.length;
        counter.setAttribute('aria-label', `${bookmarks.length} saved properties`);
    });
};

// Show toast message
const showToastMessage = (message, isError = false) => {
    const messageEl = document.querySelector(SELECTORS.message);

    if (!messageEl) {
        console.error('Message element not found');

        return;
    }

    messageEl.textContent = message;
    messageEl.setAttribute('role', 'status');
    messageEl.setAttribute('aria-live', 'polite');

    if (isError) {
        messageEl.classList.add('border-l-4', 'border-red-500');
    } else {
        messageEl.classList.remove('border-l-4', 'border-red-500');
    }

    messageEl.classList.add(CLASSES.isShown);

    setTimeout(() => {
        messageEl.classList.remove(CLASSES.isShown);
        setTimeout(() => {
            messageEl.textContent = '';
            messageEl.classList.remove('border-l-4', 'border-red-500');
        }, 300);
    }, MESSAGE_DURATION);
};

// Toggle panel
const togglePanel = (panel, backdrop, isOpen) => {
    const toggleBtns = document.querySelectorAll(SELECTORS.toggleBtn);

    if (isOpen) {
        panel.classList.add(CLASSES.isOpen);
        backdrop.classList.add(CLASSES.isOpen);
        backdrop.style.opacity = '1';
        backdrop.style.pointerEvents = 'auto';

        if (lenisInstance) lenisInstance.stop();

        panel.setAttribute('tabindex', '-1');
        panel.focus();

        toggleBtns.forEach((btn) => btn.setAttribute('aria-expanded', 'true'));
    } else {
        panel.classList.remove(CLASSES.isOpen);
        backdrop.classList.remove(CLASSES.isOpen);
        backdrop.style.opacity = '0';
        backdrop.style.pointerEvents = 'none';

        if (lenisInstance) lenisInstance.start();

        toggleBtns.forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
    }
};

// Update add-button selected state based on whether the current page is bookmarked
const updateAddButtonState = () => {
    const pageData = getCurrentPageData();
    const isSelected = isBookmarkExists(pageData.url);
    const addBtns = document.querySelectorAll(SELECTORS.addBtn);

    addBtns.forEach((btn) => btn.classList.toggle('is-selected', isSelected));
};

// Add bookmark
const addBookmark = (panel) => {
    const pageData = getCurrentPageData();

    if (isBookmarkExists(pageData.url)) {
        showToastMessage('This property is already in your favorites', false);

        return;
    }

    const bookmarks = getBookmarks();
    bookmarks.push(pageData);

    if (saveBookmarks(bookmarks)) {
        updateBookmarksDisplay(panel);
        updateAddButtonState();
        showToastMessage('Property added to favorites!', false);
    } else {
        showToastMessage('Failed to save bookmark. Please try again.', true);
    }
};

// Delete bookmark
const deleteBookmark = (index, panel) => {
    const bookmarks = getBookmarks();

    if (index >= 0 && index < bookmarks.length) {
        bookmarks.splice(index, 1);

        if (saveBookmarks(bookmarks)) {
            updateBookmarksDisplay(panel);
            updateAddButtonState();
            showToastMessage('Property removed from favorites', false);
        } else {
            showToastMessage('Failed to remove bookmark. Please try again.', true);
        }
    }
};

// Email bookmarks
const emailBookmarks = () => {
    const bookmarks = getBookmarks();

    if (bookmarks.length === 0) {
        showToastMessage('No properties to share', true);

        return;
    }

    const emailBody = bookmarks
        .map((bookmark) => {
            if (bookmark.type === 'ranch') {
                return `${escapeHtml(bookmark.name)} - ${escapeHtml(bookmark.address)}\n${escapeHtml(bookmark.price)}\n${window.location.origin}${bookmark.url}`;
            }

            return `${escapeHtml(bookmark.title)}\n${window.location.origin}${bookmark.url}`;
        })
        .join('\n\n');

    const subject = 'Your Favorite Mirr Ranch Properties';
    const mailtoLink = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(emailBody)}`;

    window.location.href = mailtoLink;
};

// Handle keyboard navigation
const handleKeyPress = (e, panel, backdrop) => {
    if (e.key === 'Escape' && panel.classList.contains(CLASSES.isOpen)) {
        togglePanel(panel, backdrop, false);
    }

    if (panel.classList.contains(CLASSES.isOpen) && e.key === 'Tab') {
        const focusableElements = panel.querySelectorAll('button, a, input, select, textarea, [tabindex]:not([tabindex="-1"])');
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
        }
    }
};

// Initialize favorites functionality
export const initFavorites = (block) => {
    const panel = block;
    const backdrop = document.querySelector(SELECTORS.backdrop);

    if (!panel || !backdrop) {
        console.error('Required favorites panel elements not found');

        return;
    }

    // Set aria attributes only on interactive toggle buttons, not the backdrop
    const toggleBtns = document.querySelectorAll(SELECTORS.toggleBtn);
    toggleBtns.forEach((btn) => {
        btn.setAttribute('aria-expanded', 'false');
        btn.setAttribute('aria-controls', 'favorites-panel');
    });

    panel.setAttribute('id', 'favorites-panel');
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-label', 'Saved properties panel');

    updateBookmarksDisplay(panel);
    updateAddButtonState();

    const clickHandler = (e) => {
        // Toggle panel via dedicated toggle buttons
        if (e.target.closest(SELECTORS.toggleBtn)) {
            e.preventDefault();
            const isOpen = panel.classList.contains(CLASSES.isOpen);
            togglePanel(panel, backdrop, !isOpen);

            return;
        }

        // Dismiss panel via backdrop click
        if (e.target.closest(SELECTORS.backdrop)) {
            togglePanel(panel, backdrop, false);

            return;
        }

        // Add bookmark
        if (e.target.closest(SELECTORS.addBtn)) {
            e.preventDefault();
            addBookmark(panel);

            return;
        }

        // Delete bookmark
        if (e.target.closest(SELECTORS.deleteBtn)) {
            e.preventDefault();
            const deleteBtn = e.target.closest(SELECTORS.deleteBtn);
            const index = parseInt(deleteBtn.dataset.bookmarkIndex, 10);
            deleteBookmark(index, panel);

            return;
        }

        // Email bookmarks
        if (e.target.closest(SELECTORS.emailBtn)) {
            e.preventDefault();
            emailBookmarks();
        }
    };

    const keydownHandler = (e) => handleKeyPress(e, panel, backdrop);

    document.addEventListener('click', clickHandler);
    document.addEventListener('keydown', keydownHandler);

    eventListeners.set(panel, { clickHandler, keydownHandler });

    return () => {
        const listeners = eventListeners.get(panel);
        if (listeners) {
            document.removeEventListener('click', listeners.clickHandler);
            document.removeEventListener('keydown', listeners.keydownHandler);
            eventListeners.delete(panel);
        }

        if (panel.classList.contains(CLASSES.isOpen) && lenisInstance) {
            lenisInstance.start();
        }
    };
};

// Cleanup all event listeners (call this when destroying the component)
export const cleanupFavorites = () => {
    eventListeners.forEach((listeners) => {
        document.removeEventListener('click', listeners.clickHandler);
        document.removeEventListener('keydown', listeners.keydownHandler);
    });
    eventListeners.clear();
};

export const Favorites = () => {
    document.querySelectorAll(SELECTORS.panel).forEach((block) => {
        initFavorites(block);
    });
};
